import { type FileShareClient } from "@anna-money/anna-web-lib/api";
import { makeAutoObservable, runInAction } from "mobx";
import type { Config } from "config/config";
import { type AddressClient } from "services/address/addressClient";
import { AddressUpdateData, type PoaAddressData, PoaRejectResult } from "services/address/addressTypes";
import { type AddressVerificationStore } from "services/addressVerification/addressVerificationStore";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type CompanyStore } from "services/company/companyStore";
import { type DirectorAddressStore } from "services/director/directorAddressStore";
import { type DirectorStore } from "services/director/directorStore";
import { type FeatureStore } from "services/feature/featureStore";

type PoaUploadState = "idle" | "uploading" | "polling" | "success" | "error";
export type PoaAddressChoice = "user" | "document";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

export class DirectorPoaStore {
  private _poaFile?: File = undefined;
  private _uploadState: PoaUploadState = "idle";
  private _poaErrorType?: PoaRejectResult = undefined;
  private _poaAddress?: PoaAddressData = undefined;
  private _selectedAddressChoice?: PoaAddressChoice = undefined;
  private _pollTimer?: ReturnType<typeof setTimeout> = undefined;
  private _pollCancelled = false;
  private _pollWaitResolve?: () => void = undefined;

  constructor(
    private readonly _addressClient: AddressClient,
    private readonly _companyStore: CompanyStore,
    private readonly _directorStore: DirectorStore,
    private readonly _directorAddressStore: DirectorAddressStore,
    private readonly _featureStore: FeatureStore,
    private readonly _fileShareClient: FileShareClient,
    private readonly _analyticsManager: AnalyticsManager,
    private readonly _addressVerificationStore: AddressVerificationStore,
    private readonly _config: Config,
  ) {
    makeAutoObservable(this);
  }

  get targetDirectorId(): string {
    return this._directorStore.getMainDirector().id;
  }

  get poaFile(): File | undefined {
    return this._poaFile;
  }

  get uploadState(): PoaUploadState {
    return this._uploadState;
  }

  get poaErrorType(): PoaRejectResult | undefined {
    return this._poaErrorType;
  }

  get poaAddress(): PoaAddressData | undefined {
    return this._poaAddress;
  }

  get selectedAddressChoice(): PoaAddressChoice | undefined {
    return this._selectedAddressChoice;
  }

  set selectedAddressChoice(value: PoaAddressChoice | undefined) {
    this._selectedAddressChoice = value;
  }

  get isAddressMismatch(): boolean {
    return this._poaErrorType === PoaRejectResult.NameMismatch && !!this._poaAddress;
  }

  private get _addressId(): string {
    const address = this._directorAddressStore.tryGetForDirector(this.targetDirectorId);
    if (!address) {
      throw new Error("Director address not found");
    }
    return address.id;
  }

  private get _isPoaVerificationEnabled(): boolean {
    return this._featureStore.hasFeature("formationPoaVerification");
  }

  // The multi-director / re-verify-on-edit behaviour is GB-only and opt-in behind the feature flag.
  get isStrictPoaRules(): boolean {
    return !this._config.isAU && this._isPoaVerificationEnabled;
  }

  async onChange(file: File): Promise<void> {
    if (!file) {
      this._analyticsManager.event("poa-error-no-file");
      return;
    }

    if (this._isPoaVerificationEnabled) {
      await this._onChangeWithVerification(file);
    } else {
      await this._onChangeLegacy(file);
    }
  }

  private async _onChangeLegacy(file: File): Promise<void> {
    try {
      const completeResponse = await this._fileShareClient.uploadFile(file);
      await this._companyStore.updatePoa(completeResponse.id);

      this._poaFile = file;
      this._analyticsManager.event("address_check_submission", { file: completeResponse.id });
    } catch (e) {
      console.error(e);
      this._analyticsManager.event("fail-poa", { result: e instanceof Error ? e.message : "unknown_error" });
    }
  }

  private async _onChangeWithVerification(file: File): Promise<void> {
    this._uploadState = "uploading";
    this._poaErrorType = undefined;
    this._poaAddress = undefined;

    try {
      const completeResponse = await this._fileShareClient.uploadFile(file);
      const directorId = this.targetDirectorId;
      const address = this._directorAddressStore.tryGetForDirector(directorId);
      if (!address) {
        throw new Error("Director address not found");
      }
      await this._addressClient.updateAddress(
        address.id,
        new AddressUpdateData({
          premise: address.premise,
          street: address.street,
          town: address.town,
          postcode: address.postcode,
          country: address.country,
          poaFileId: completeResponse.id,
        }),
      );

      runInAction(() => {
        this._poaFile = file;
        this._uploadState = "polling";
      });

      this._analyticsManager.event("address_check_submission", { file: completeResponse.id });
      await this._pollForResults(address.id);
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this._poaFile = file;
        this._uploadState = "error";
      });
      this._analyticsManager.event("fail-poa", { result: e instanceof Error ? e.message : "unknown_error" });
    }
  }

  retryUpload(): void {
    const previousError = this._poaErrorType;
    this._resetUploadState();

    this._analyticsManager.event("poa_retry_upload", { previous_error: previousError });
  }

  async onAddressEdited(): Promise<void> {
    // A registered-address edit invalidates any prior verification on the backend, so drop the cached
    // upload/check state and re-check the main director's address instead of trusting it.
    const directorId = this.targetDirectorId;
    this._resetUploadState();
    this._addressVerificationStore.clearForDirector(directorId);
    await this._directorAddressStore.loadForDirector(directorId);
    await this._addressVerificationStore.checkDirectorAddress();
    this._analyticsManager.event("poa_address_edited", { director_id: directorId });
  }

  private _resetUploadState(): void {
    this._poaFile = undefined;
    this._uploadState = "idle";
    this._poaErrorType = undefined;
    this._poaAddress = undefined;
    this._selectedAddressChoice = undefined;
    this._stopPolling();
  }

  async submitAddressChoice(): Promise<void> {
    if (!this._selectedAddressChoice) {
      return;
    }

    if (this._selectedAddressChoice === "document" && this._poaAddress) {
      const directorId = this.targetDirectorId;
      await this._addressClient.updateAddress(
        this._addressId,
        new AddressUpdateData({
          town: this._poaAddress.city || "",
          postcode: this._poaAddress.postcode || "",
          premise: this._poaAddress.buildingNumber || "",
          street: this._poaAddress.street || "",
          country: this._poaAddress.country,
        }),
      );

      await this._directorAddressStore.loadForDirector(directorId);

      if (this.isStrictPoaRules) {
        // New flow: editing the address resets verification on the backend — re-check instead of assuming success.
        this._analyticsManager.event("poa_address_mismatch_choice", { choice: this._selectedAddressChoice });
        this._addressVerificationStore.clearForDirector(directorId);
        const stillRequiresPoa = await this._addressVerificationStore.checkDirectorAddress();

        runInAction(() => {
          this._uploadState = stillRequiresPoa ? "idle" : "success";
          this._poaErrorType = undefined;
          this._selectedAddressChoice = undefined;
          this._poaAddress = undefined;
        });
        return;
      }
    }

    this._analyticsManager.event("poa_address_mismatch_choice", { choice: this._selectedAddressChoice });

    runInAction(() => {
      this._uploadState = "success";
      this._poaErrorType = undefined;
    });
  }

  dispose(): void {
    this._stopPolling();
  }

  private async _pollForResults(addressId: string): Promise<void> {
    this._stopPolling();
    this._pollCancelled = false;
    this._analyticsManager.event("poa_polling_started");

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await this._wait(POLL_INTERVAL_MS);
      if (this._pollCancelled) {
        this._analyticsManager.event("poa_polling_cancelled");
        return;
      }

      try {
        const address = await this._addressClient.getAddressById(addressId);

        if (address.isPoaPassedChecks === true) {
          // New flow only: refresh the cached address so gating (and the POA target director) reflects the pass.
          if (this.isStrictPoaRules) {
            await this._directorAddressStore.loadForDirector(this.targetDirectorId);
          }
          runInAction(() => {
            this._uploadState = "success";
          });
          this._analyticsManager.event("poa_check_result", {
            status: "passed",
            address_id: addressId,
          });
          return;
        }

        if (address.isPoaPassedChecks === false && address.poaRejectResult) {
          runInAction(() => {
            this._uploadState = "error";
            this._poaErrorType = address.poaRejectResult as PoaRejectResult;
            this._poaAddress = address.poaAddress;
          });
          this._analyticsManager.event("poa_check_result", {
            status: "rejected",
            reject_result: address.poaRejectResult,
            address_id: addressId,
          });
          return;
        }
      } catch (e) {
        console.error("Error polling POA results", e);
        this._analyticsManager.event("poa_polling_error", { error: e instanceof Error ? e.message : "unknown_error" });
      }
    }

    runInAction(() => {
      this._uploadState = "error";
    });
  }

  private _stopPolling(): void {
    this._pollCancelled = true;
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = undefined;
    }
    if (this._pollWaitResolve) {
      this._pollWaitResolve();
      this._pollWaitResolve = undefined;
    }
  }

  private async _wait(ms: number): Promise<void> {
    await new Promise<void>((resolve) => {
      this._pollWaitResolve = resolve;
      this._pollTimer = setTimeout(() => {
        this._pollWaitResolve = undefined;
        resolve();
      }, ms);
    });
  }
}
